import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import { INPUT_KINDS } from '../lib/fieldTypes';
import SignatureModal from './SignatureModal';
import SignFieldsOverlay from './SignFieldsOverlay';

/**
 * Shared guest-signing surface: the field overlay + signature modal + the
 * required-fields counter with guided "Next" scrolling and the submit bar
 * (OtterWaiver's PdfSignatureStep behavior, used by both agreement and waiver
 * flows).
 */
export default function SigningFlow({ pdf, fields, signer, submitting, onSubmit }) {
  const [values, setValues] = useState({});
  const [modalField, setModalField] = useState(null);

  // Render pages at the pane's own width so the document always fits — the old
  // one-shot `window.innerWidth` read never survived a rotate or resize.
  const paneRef = useRef(null);
  const [renderWidth, setRenderWidth] = useState(760);
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      // contentRect already excludes the pane's p-4 padding. Below 320px the
      // page would be unreadable, so hold that floor and let the pane pan.
      const width = Math.floor(entry.contentRect.width);
      if (width > 0) setRenderWidth(Math.max(320, width));
    });
    observer.observe(pane);
    return () => observer.disconnect();
  }, []);

  const setValue = (fieldId, value) => setValues((prev) => ({ ...prev, [fieldId]: value }));

  const missingRequired = useMemo(() => {
    const satisfiedGroups = new Set(
      fields
        .filter((f) => f.kind === 'radio' && f.radioGroup && values[f.id] === 'true')
        .map((f) => f.radioGroup),
    );
    const seenGroups = new Set();
    return fields.filter((field) => {
      if (!field.required || !INPUT_KINDS.has(field.kind)) return false;
      const value = values[field.id] ?? '';
      if (field.kind === 'radio') {
        if (!field.radioGroup) return value !== 'true';
        if (seenGroups.has(field.radioGroup)) return false; // one entry per group
        seenGroups.add(field.radioGroup);
        return !satisfiedGroups.has(field.radioGroup);
      }
      if (field.kind === 'checkbox') return value !== 'true';
      return !value.trim();
    });
  }, [fields, values]);

  // The COMPLETED DATA ENTRY moment on the audit certificate: the first time
  // every required field is satisfied. Held in a ref so a later edit that
  // briefly re-opens a field doesn't move the recorded time forward.
  const dataEntryCompletedAt = useRef(null);
  useEffect(() => {
    const hasEntered = Object.values(values).some((value) => value !== '');
    if (hasEntered && missingRequired.length === 0 && !dataEntryCompletedAt.current) {
      dataEntryCompletedAt.current = new Date().toISOString();
    }
  }, [missingRequired, values]);

  const scrollToNext = () => {
    const next = missingRequired[0];
    if (!next) return;
    document
      .querySelector(`[data-signfield="${next.id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const submit = () => {
    const payload = Object.entries(values)
      .filter(([, value]) => value !== '')
      .map(([fieldId, value]) => ({ fieldId, value }));
    onSubmit(payload, {
      dataEntryCompletedAt: dataEntryCompletedAt.current ?? new Date().toISOString(),
    });
  };

  return (
    <div className="space-y-4">
      <div ref={paneRef} className="overflow-x-auto rounded-xl bg-slate-200/60 p-4">
        <SignFieldsOverlay
          pdf={pdf}
          fields={fields}
          values={values}
          onChange={setValue}
          onSignatureClick={setModalField}
          signer={signer}
          width={renderWidth}
        />
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-3 z-10 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 shadow-lg">
        <p className="min-w-0 flex-1 text-sm text-slate-600">
          {missingRequired.length > 0 ? (
            <>
              <span className="font-semibold text-slate-900">{missingRequired.length}</span>{' '}
              required field{missingRequired.length === 1 ? '' : 's'} left
            </>
          ) : (
            'All required fields are complete'
          )}
        </p>
        {missingRequired.length > 0 ? (
          <Button variant="secondary" size="sm" onClick={scrollToNext}>
            Next field
          </Button>
        ) : null}
        <Button
          size="sm"
          disabled={missingRequired.length > 0}
          loading={submitting}
          onClick={submit}
        >
          Finish & sign
        </Button>
      </div>

      <SignatureModal
        open={Boolean(modalField)}
        onClose={() => setModalField(null)}
        kind={modalField?.kind}
        defaultName={
          modalField?.kind === 'initial'
            ? `${signer.firstName?.[0] ?? ''}${signer.lastName?.[0] ?? ''}`.toUpperCase()
            : `${signer.firstName} ${signer.lastName ?? ''}`.trim()
        }
        onComplete={(value) => modalField && setValue(modalField.id, value)}
      />
    </div>
  );
}
